/**
 * @fileoverview Founding Ensemble enrolment — the private sign-up flow.
 *
 * **This route is deliberately linked from NOWHERE.** Not the public nav, not
 * the sign-in tab strip, not the sitemap, not the studio sidebar. The only way
 * here is a token the founder emailed personally, and holding that URL is the
 * entire membership test. Anything that links to it — including a "founding
 * members sign up here" line on the pricing page — breaks the one property the
 * cohort is built on.
 *
 * **Two screens, and it hands straight over to the normal onboarding.** It
 * used to be three, and the third asked about the teacher's studio — which
 * made it the first of THREE consecutive "tell us about your teaching" screens,
 * since onboarding then asks the same kind of question twice more. So the band
 * question moved into onboarding beside the other studio fields, and the
 * "hardest part" question moved to the welcome page, where declining it costs
 * nothing. What is left here is the invitation and the account: the two things
 * that genuinely cannot happen anywhere else.
 *
 * The progress rail spans the WHOLE journey — these two screens are 1 and 2 of
 * six, and onboarding picks up at three. Two bars, one finishing at 3 of 3 and
 * the next restarting at 1 of 4, is what made finishing the last step feel like
 * discovering four more. Motion variants are imported from `OnboardingPage`
 * rather than re-declared, because two copies of a transition drift and the
 * whole point is that these feel identical.
 *
 * **Step 2 asks a different question of a teacher who already uses Hans.** A
 * hand-picked invitee may well have a roster and a year of lesson history
 * already, and offering them a create-an-account form is an offer to duplicate
 * their own studio — which the server then refuses, after they have chosen a
 * password. So `has_account` on the invitation forks the second screen into a
 * sign-in, their existing account is upgraded in place, and the screen leads
 * with what does NOT change, because "will this touch my students?" is the only
 * question a teacher has at that moment.
 *
 * Two consequences of that fork are decisions rather than layout:
 *
 * * **The sign-in step re-gates nothing.** No terms checkbox: they accepted at
 *   signup, and asking again would imply the invitation had changed the deal.
 * * **Nothing here redeems the invitation.** Signing in does, server-side, on
 *   whichever path they use — so the Google button is the ordinary redirect and
 *   needs no token smuggled through it, and a teacher who closes this tab and
 *   signs in tomorrow from a bookmark still gets their membership.
 *
 * Three things about the form are decisions rather than styling:
 *
 * * **The email is shown, not asked for.** It comes from the invitation, the
 *   server ignores anything else, and displaying it locked is what tells the
 *   teacher this was meant for them personally rather than being a form anyone
 *   could fill in.
 * * **It stays the shortest sign-up in the product.** Splitting it across
 *   steps must not become an excuse to ask more; it is the same four fields,
 *   paced.
 * * **The hard-part question is optional and says so.** A required essay
 *   question on a sign-up form is how you lose the sign-up.
 *
 * Uses bare axios, like every other public page: the shared `lib/api` client's
 * 401 interceptor would bounce an unauthenticated visitor to the landing page.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";
import FoundingMedal from "@/components/FoundingMedal";
// Two enrolment screens, four onboarding ones — counted in one place so the
// two pages that draw the rail cannot disagree about how long the journey is.
import { fadeUp, JOURNEY_STEPS, slideVariants } from "@/lib/onboardingMotion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


// Kept in step with `PERKS` in `backend/services/founding_email.py` — this is
// the page that email links to, and a teacher who reads one promise in the
// invitation and a different one here has been told two things, and will
// believe the smaller.
// The heading is worded differently here than in the email on purpose — same
// promise, read in a different voice — so only the LIST is pinned equal across
// the two surfaces.
const GOOGLE_CLIENT_ID = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;

// What an existing account keeps. Spelled out rather than implied, because the
// invitation arrives at a teacher who has already put a term of work into Hans,
// and "will accepting this touch my students?" is the only thing they want
// answered before they type a password.
const KEEPS = [
  "Every student, lesson and summary exactly where it is",
  "Your schedule, invoices and consent records untouched",
  "The same sign-in you already use",
];

const PERKS_HEADING = "As a member of the Founding Ensemble, you'll get:";
const PERKS = [
  // Where the offer's price and duration are stated. They used to sit in prose
  // above this list; keeping them IN the list means they ride the same
  // element-wise check that keeps the email and this page in step.
  "12 months of the full paid subscription, on us",
  "A permanent Founding Ensemble badge on your profile",
  "A direct line to shape what we build next",
  "Exclusive access to new features and workshops",
];

const FoundingInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  // Set only by the enrolment 409 — the race where an account appears between
  // this page loading and the form being submitted. It never turns back off:
  // an account that exists cannot stop existing, and flipping back would put
  // the create-account form in front of somebody it can only fail for.
  const [accountFound, setAccountFound] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    tos_accepted: false,
  });

  // Which second screen this is: sign in and be upgraded, or create the account.
  const signInMode = accountFound || invite?.has_account === true;

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/founding/invite/${token}`)
      .then((res) => {
        if (cancelled) return;
        setInvite(res.data);
        // Pre-split the name we were given so they can correct it rather than
        // retype it. The invitation carries whatever the founder typed.
        const [first = "", ...rest] = (res.data.name || "").trim().split(/\s+/);
        setForm((f) => ({ ...f, first_name: first, last_name: rest.join(" ") }));
      })
      .catch(() => {
        if (!cancelled) setInvalid(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Each step gates only on what it actually asked for, so nobody is blocked by
  // a field they have not reached yet.
  //
  // Sign-in mode asks for less, and every omission is deliberate: no name (the
  // account has one), no terms checkbox (they accepted at signup, and re-asking
  // would imply the invitation changed the deal), and the login minimum rather
  // than the signup one, so an account made under the old six-character policy
  // can still get in.
  const stepValid = (s) => {
    if (s !== 2) return true;
    if (signInMode) return form.password.length >= 6;
    return form.first_name.trim() && form.password.length >= 8 && form.tos_accepted;
  };

  const goNext = () => {
    if (!stepValid(step)) return;
    setDirection(1);
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  /**
   * Sign in an account that already exists, and let the server upgrade it.
   *
   * This posts to the ORDINARY login endpoint, not to anything of the cohort's
   * own. Redemption happens server-side on every sign-in path, so there is one
   * set of rules about passwords, unverified addresses and OAuth-only accounts
   * rather than a second copy here that would drift from it — and a teacher who
   * abandons this page and signs in tomorrow is upgraded just the same.
   *
   * They land on the dashboard rather than onboarding: they finished that a
   * term ago. `welcome` opens the founding introduction over it, which is the
   * one part of the arrival they have not seen.
   */
  const signIn = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/login`,
        { email: invite.email, password: form.password },
        { withCredentials: true },
      );
      navigate("/dashboard", {
        replace: true,
        state: { user: res.data.user, welcome: res.data.founding_claimed === true },
      });
    } catch (err) {
      // An unverified account is bounced to the code screen exactly as the
      // ordinary login does — the invitation cannot wave that through, and the
      // backend has already re-sent a fresh code by the time we get here.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "email_not_verified") {
        navigate("/verify-email", { state: { email: detail.email || invite.email } });
        return;
      }
      toast.error(getErrorMessage(err, "We couldn't sign you in."));
      setSubmitting(false);
    }
  };

  /**
   * Sign in with Google, for an invited teacher whose account has no password.
   *
   * Deliberately the ordinary redirect, with nothing of the invitation smuggled
   * through it: the token does not need to survive the round trip, because the
   * server redeems by email on the way back in. So this is the same three lines
   * the sign-in page runs, and the callback lands them on the dashboard with
   * their membership already applied.
   */
  const signInWithGoogle = () => {
    // "Continue with Google" doubles as sign-up, and this page shows no terms
    // checkbox in sign-in mode — so the stash carries the acceptance the notice
    // under the button gives, for the race where the account has vanished.
    sessionStorage.setItem("hans_tos_accepted", "1");
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: window.location.origin,
      response_type: "code",
      scope: "email profile",
      prompt: "select_account",
      access_type: "online",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const enroll = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/founding/enroll`,
        { token, ...form },
        { withCredentials: true },
      );
      // Straight into the app with a live session — no verification code, since
      // the token arrived at this address and already proved it. `state.user`
      // is what App's AuthProvider consumes without re-fetching.
      navigate("/onboarding", { replace: true, state: { user: res.data.user } });
    } catch (err) {
      // The account appeared underneath us. Switch the screen to the sign-in it
      // should have been rather than only reporting the refusal — the remedy is
      // one field away, and a toast over a form that can no longer work is a
      // dead end.
      if (err.response?.status === 409 && err.response?.data?.detail?.code === "account_exists") {
        setAccountFound(true);
        setForm((f) => ({ ...f, password: "" }));
      }
      toast.error(getErrorMessage(err, "We couldn't complete your sign-up."));
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!stepValid(2) || submitting) return;
    return signInMode ? signIn() : enroll();
  };

  if (loading) {
    return (
      <Shell>
        <Loader2 className="h-6 w-6 animate-spin text-[var(--accent-yellow-text)]" />
      </Shell>
    );
  }

  if (invalid || !invite) {
    return (
      <Shell>
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl font-light text-[var(--text-primary)]">This invitation isn't valid</h1>
          {/* Deliberately vague, and it matches the server's single 404: the
              link may be expired, already used, or withdrawn, and saying which
              would let anyone holding a guessed token learn whether it was
              real. The remedy is the same in every case. */}
          <p className="mt-3 font-sans text-sm text-[var(--text-tertiary)]">
            It may have expired or already been used. Reply to the email that brought you here and we'll send a fresh
            one.
          </p>
        </div>
      </Shell>
    );
  }

  const firstName = (invite.name || "").trim().split(/\s+/)[0];

  return (
    <Shell>
      {/* Progress bar — same fixed rail as onboarding, so the two flows read as
          one journey rather than two products. */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-[var(--studio-raised)]">
        <motion.div
          className="h-full bg-[var(--accent-yellow)]"
          animate={{ width: `${(step / JOURNEY_STEPS) * 100}%` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          data-testid="founding-progress"
          // The width is animated, so it is not readable synchronously — these
          // carry the same fact in a form a test (and a screen reader audit)
          // can actually inspect.
          data-step={step}
          data-total={JOURNEY_STEPS}
        />
      </div>

      <div className="w-full max-w-lg">
        <AnimatePresence mode="wait" custom={direction}>
          {/* ── Step 1: the invitation ── */}
          {step === 1 && (
            <motion.div
              key="step1"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="text-center"
            >
              <motion.div variants={fadeUp} className="mb-6 flex justify-center">
                <FoundingMedal size={72} />
              </motion.div>
              <motion.p
                variants={fadeUp}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-yellow-text)]"
              >
                Founding Ensemble
              </motion.p>
              <motion.h1
                variants={fadeUp}
                className="mt-3 font-serif text-[28px] font-light leading-tight text-[var(--text-primary)]"
              >
                {firstName ? `You're invited, ${firstName}` : "You're invited"}
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mt-3 font-sans text-sm leading-relaxed text-[var(--text-secondary)]"
              >
                As we launch Hans, we're assembling a select group of musicians who are passionate about how we define
                modern music education.
              </motion.p>

              {/* The positioning line, shared word-for-word with the invitation
                  email that links here — the two are read seconds apart. */}
              <motion.p
                variants={fadeUp}
                className="mt-3 font-sans text-sm leading-relaxed text-[var(--text-secondary)]"
              >
                Hans gives private music teachers one organized platform for their students, schedule, lesson history,
                and teaching workflow — so you can focus on teaching your next lesson.
              </motion.p>

              <motion.p
                variants={fadeUp}
                className="mt-7 text-left font-sans text-xs font-semibold text-[var(--text-tertiary)]"
              >
                {PERKS_HEADING}
              </motion.p>
              <motion.ul
                variants={fadeUp}
                className="mt-2 space-y-2.5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 text-left"
              >
                {PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent-yellow-text)]" />
                    <span className="font-sans text-sm text-[var(--text-secondary)]">{perk}</span>
                  </li>
                ))}
              </motion.ul>

              <motion.div variants={fadeUp} className="mt-6">
                <Label className="font-sans text-xs text-[var(--text-tertiary)]">Your invitation was sent to</Label>
                {/* Read-only rather than a disabled input: the address is not
                    being collected, it is being confirmed. */}
                <div
                  data-testid="founding-email"
                  className="mt-1.5 rounded-[10px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3.5 py-2.5 text-center font-mono text-sm text-[var(--text-secondary)]"
                >
                  {invite.email}
                </div>
              </motion.div>

              <motion.div variants={fadeUp}>
                <StepButton onClick={goNext} testId="founding-next">
                  Accept your invitation
                </StepButton>
              </motion.div>
            </motion.div>
          )}

          {/* ── Step 2: the account, and the terms ── */}
          {step === 2 && (
            <motion.form
              key="step2"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: "easeInOut" }}
              onSubmit={submit}
            >
              <StepHeading
                eyebrow={`Step 2 of ${JOURNEY_STEPS}`}
                title={signInMode ? "Sign in to accept" : "Create your account"}
              />

              {signInMode && <KeepsPanel email={invite.email} />}

              {!signInMode && (
              <motion.div variants={fadeUp} className="mt-7 grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name" className="font-sans text-xs text-[var(--text-tertiary)]">
                    First name
                  </Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={update("first_name")}
                    className="mt-1.5"
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="font-sans text-xs text-[var(--text-tertiary)]">
                    Last name
                  </Label>
                  <Input id="last_name" value={form.last_name} onChange={update("last_name")} className="mt-1.5" />
                </div>
              </motion.div>
              )}

              <motion.div variants={fadeUp} className="mt-5">
                <Label htmlFor="password" className="font-sans text-xs text-[var(--text-tertiary)]">
                  {signInMode ? "Your password" : "Choose a password"}
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={update("password")}
                    /* No Enter handler: the form's own submit already covers it,
                       and the one that used to sit here called `goNext` — left
                       over from when this was not the last step, so pressing
                       Enter would advance to a step three that renders nothing.
                       Not reproducible in jsdom (userEvent's implicit
                       submission never reaches the React keydown), which is why
                       it survived; a browser fires keydown before submitting. */
                    minLength={signInMode ? 6 : 8}
                    autoComplete={signInMode ? "current-password" : "new-password"}
                    autoFocus={signInMode}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {signInMode ? (
                  <div className="mt-1.5 text-right">
                    <Link
                      to="/forgot-password"
                      className="font-sans text-xs text-[var(--accent-yellow-text)] hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                ) : (
                  <p className="mt-1.5 font-sans text-xs text-[var(--text-tertiary)]">At least 8 characters.</p>
                )}
              </motion.div>

              {/* Terms are asked once, at signup. An existing account already
                  accepted them, and re-gating here would suggest the invitation
                  had changed the deal — which it has not. */}
              {!signInMode && (
              <motion.label variants={fadeUp} className="mt-5 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={form.tos_accepted}
                  onChange={update("tos_accepted")}
                  className="mt-0.5 h-4 w-4 accent-[var(--accent-yellow)]"
                />
                <span className="font-sans text-xs leading-relaxed text-[var(--text-tertiary)]">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-[var(--accent-yellow-text)] hover:underline">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[var(--accent-yellow-text)] hover:underline">
                    Privacy Policy
                  </a>
                  .
                </span>
              </motion.label>
              )}

              <motion.div variants={fadeUp}>
                <StepButton
                  type="submit"
                  disabled={!stepValid(2) || submitting}
                  testId="founding-submit"
                  onBack={goBack}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : signInMode ? (
                    "Sign in and accept"
                  ) : (
                    "Join the Founding Ensemble"
                  )}
                </StepButton>
              </motion.div>

              {/* The account may have no password at all — an invited teacher
                  who has always used Google. Without this the sign-in step is a
                  form they can never complete.

                  Rendered unconditionally, exactly as the sign-in page renders
                  its own: a missing client id is a broken build everywhere at
                  once, and hiding this one button would quietly remove the only
                  way in for the accounts that need it most. */}
              {signInMode && (
                <motion.div variants={fadeUp} className="mt-5">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[var(--studio-divider)]" />
                    <span className="font-sans text-xs text-[var(--text-tertiary)]">or</span>
                    <div className="h-px flex-1 bg-[var(--studio-divider)]" />
                  </div>
                  <Button
                    type="button"
                    onClick={signInWithGoogle}
                    data-testid="founding-google"
                    className="w-full rounded-full bg-[var(--text-primary)] py-3 font-sans text-sm font-medium text-[var(--studio-base)] transition-transform hover:scale-105 active:scale-95"
                  >
                    Continue with Google
                  </Button>
                  <p className="mt-3 text-center font-sans text-xs leading-relaxed text-[var(--text-tertiary)]">
                    Sign in the way you always do — your Founding Ensemble membership is added either way.
                  </p>
                </motion.div>
              )}
            </motion.form>
          )}

        </AnimatePresence>
      </div>
    </Shell>
  );
};

/**
 * What an existing account keeps, stated before anything is asked of it.
 *
 * The one question a teacher has at this moment is whether accepting will touch
 * the studio they have already built here — and a screen that answers it only
 * by NOT mentioning students is a screen they read twice and still don't trust.
 * So the reassurance leads, and the password field follows it.
 */
function KeepsPanel({ email }) {
  return (
    <>
      <motion.p variants={fadeUp} className="mt-6 text-center font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
        You already teach with Hans, so there's nothing to set up. Sign in and we'll add your Founding Ensemble
        membership to the account you have.
      </motion.p>

      <motion.ul
        variants={fadeUp}
        data-testid="founding-keeps"
        className="mt-5 space-y-2.5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 text-left"
      >
        {KEEPS.map((keep) => (
          <li key={keep} className="flex items-start gap-3">
            {/* The shield, not a colour, is what separates this list from
                the perks on step one — `--state-success` would mean something
                succeeded, and nothing here has happened yet. */}
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent-yellow-text)]" />
            <span className="font-sans text-sm text-[var(--text-secondary)]">{keep}</span>
          </li>
        ))}
      </motion.ul>

      <motion.div variants={fadeUp} className="mt-6">
        <Label className="font-sans text-xs text-[var(--text-tertiary)]">Signing in as</Label>
        {/* Read-only for the same reason step 1 shows it: the address is the
            invitation's, and it is being confirmed rather than collected. */}
        <div
          data-testid="founding-signin-email"
          className="mt-1.5 rounded-[10px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3.5 py-2.5 text-center font-mono text-sm text-[var(--text-secondary)]"
        >
          {email}
        </div>
      </motion.div>
    </>
  );
}

function StepHeading({ eyebrow, title }) {
  return (
    <div className="text-center">
      <motion.div variants={fadeUp} className="mb-5 flex justify-center">
        <FoundingMedal size={48} />
      </motion.div>
      <motion.p
        variants={fadeUp}
        className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-yellow-text)]"
      >
        {eyebrow}
      </motion.p>
      <motion.h1
        variants={fadeUp}
        className="mt-2.5 font-serif text-[26px] font-light leading-tight text-[var(--text-primary)]"
      >
        {title}
      </motion.h1>
    </div>
  );
}

/** The step's forward action, with Back beside it once there is somewhere back to. */
function StepButton({ children, onClick, onBack, disabled, testId, type = "button" }) {
  return (
    <div className="mt-7 flex items-center gap-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          data-testid="founding-back"
          className="flex items-center gap-1.5 rounded-full px-4 py-3 font-sans text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}
      <Button
        type={type}
        onClick={type === "submit" ? undefined : onClick}
        disabled={disabled}
        data-testid={testId}
        className="flex-1 rounded-full bg-[var(--accent-yellow)] py-3 font-sans text-sm font-semibold text-[var(--text-inverse)] disabled:opacity-40"
      >
        {children}
        {typeof children === "string" && <ArrowRight className="ml-2 h-4 w-4" />}
      </Button>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 py-14"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.06) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, var(--studio-base)",
      }}
      data-theme="dark"
    >
      {children}
    </div>
  );
}

export default FoundingInvitePage;
